import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api, money0, shortDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusPill } from "../components/UI";
import { useAuth } from "../lib/auth";
import { useSortable, unitNumberKey } from "../lib/sortable";

function statusPill(status) {
  const map = {
    "on-hire": ["green", "ON HIRE"],
    "free": ["neutral", "FREE"],
    "being-sold": ["blue", "BEING SOLD"],
    "for-sale": ["blue", "FOR SALE"],
    "sale-pending": ["blue", "SALE PENDING"],
  };
  const [tone, label] = map[status] || ["neutral", status.toUpperCase()];
  return <StatusPill tone={tone}>{label}</StatusPill>;
}

function paperPill(dateStr) {
  if (!dateStr) return <StatusPill tone="neutral">—</StatusPill>;
  const days = Math.floor((new Date(dateStr) - new Date()) / (1000*60*60*24));
  if (days < 0) return <StatusPill tone="red">RAN OUT</StatusPill>;
  if (days <= 30) return <StatusPill tone="amber">{shortDate(dateStr).toUpperCase()}</StatusPill>;
  return <StatusPill tone="green">✓ OK</StatusPill>;
}

const OWNING_COMPANIES = ["All Cranes (Hire)","P.R Shelton Holdings","Polstat","Hyjac","Shelton Equipment","Australian Machinery Hire"];

function AddMachineDialog({ onClose, onSaved }) {
  const [f, setF] = useState({
    unit_number: "", serial: "", make: "", model_name: "", capacity_tonnes: "",
    owning_company: "", home_yard: "", rego: "", rego_expiry: "", cranesafe_due: "", ten_year_due: "",
    plant_reg: "", plant_reg_expiry: "",
    folder_link: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [downOnBackdrop, setDownOnBackdrop] = useState(false);
  const save = async () => {
    if (!f.unit_number.trim()) { setErr("Unit number is required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...f };
      if (payload.capacity_tonnes) payload.capacity_tonnes = Number(payload.capacity_tonnes);
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      await api.post("/machines", payload);
      onSaved(); onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="ach-modal-backdrop" data-testid="add-machine-dialog"
      onMouseDown={(e)=>setDownOnBackdrop(e.target === e.currentTarget)}
      onMouseUp={(e)=>{ if (downOnBackdrop && e.target === e.currentTarget) onClose(); setDownOnBackdrop(false); }}>
      <div className="ach-modal" style={{maxWidth: 640}} onMouseDown={(e)=>e.stopPropagation()} onMouseUp={(e)=>e.stopPropagation()}>
        <div className="ach-modal__header"><h2 style={{margin:0,fontSize:16,fontWeight:600}}>Add a machine</h2>
          <button className="ach-btn ach-btn--grey" onClick={onClose} style={{padding:"6px 10px"}}>Close</button></div>
        <div className="ach-modal__body">
          <div className="ach-form-grid">
            <div className="ach-field"><label>Unit number *</label>
              <input value={f.unit_number} onChange={(e)=>setF({...f, unit_number:e.target.value})} data-testid="am-unit" placeholder="e.g. ACH512" />
            </div>
            <div className="ach-field"><label>Serial number</label>
              <input value={f.serial} onChange={(e)=>setF({...f, serial:e.target.value})} data-testid="am-serial" />
            </div>
            <div className="ach-field"><label>Make</label>
              <input value={f.make} onChange={(e)=>setF({...f, make:e.target.value})} data-testid="am-make" placeholder="e.g. Franna" />
            </div>
            <div className="ach-field"><label>Model</label>
              <input value={f.model_name} onChange={(e)=>setF({...f, model_name:e.target.value})} data-testid="am-model" placeholder="e.g. AT40" />
            </div>
            <div className="ach-field"><label>Capacity (tonnes)</label>
              <input type="number" min="0" step="0.5" value={f.capacity_tonnes} onChange={(e)=>setF({...f, capacity_tonnes:e.target.value})} data-testid="am-capacity" />
            </div>
            <div className="ach-field"><label>Owned by</label>
              <select value={f.owning_company} onChange={(e)=>setF({...f, owning_company:e.target.value})} data-testid="am-owner">
                <option value="">— pick a company —</option>
                {OWNING_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="ach-field"><label>Home yard</label>
              <input value={f.home_yard} onChange={(e)=>setF({...f, home_yard:e.target.value})} data-testid="am-yard" placeholder="e.g. Newcastle depot" />
            </div>
            <div className="ach-field"><label>Rego #</label>
              <input value={f.rego} onChange={(e)=>setF({...f, rego:e.target.value})} data-testid="am-rego" />
            </div>
            <div className="ach-field"><label>Rego expiry</label>
              <input type="date" value={f.rego_expiry} onChange={(e)=>setF({...f, rego_expiry:e.target.value})} data-testid="am-rego-exp" />
            </div>
            <div className="ach-field"><label>CraneSafe due</label>
              <input type="date" value={f.cranesafe_due} onChange={(e)=>setF({...f, cranesafe_due:e.target.value})} data-testid="am-cs-due" />
            </div>
            <div className="ach-field"><label>10-year major due</label>
              <input type="date" value={f.ten_year_due} onChange={(e)=>setF({...f, ten_year_due:e.target.value})} data-testid="am-ten-year" />
            </div>
            <div className="ach-field"><label>Plant registration #</label>
              <input value={f.plant_reg} onChange={(e)=>setF({...f, plant_reg:e.target.value})} data-testid="am-plant-reg" placeholder="e.g. PR-12345" />
            </div>
            <div className="ach-field"><label>Plant reg expiry</label>
              <input type="date" value={f.plant_reg_expiry} onChange={(e)=>setF({...f, plant_reg_expiry:e.target.value})} data-testid="am-plant-reg-expiry" />
            </div>
            <div className="ach-field" style={{gridColumn:"1 / -1"}}><label>Shared folder link</label>
              <input value={f.folder_link} onChange={(e)=>setF({...f, folder_link:e.target.value})} data-testid="am-folder-link" placeholder="https://... (SharePoint / Drive / Dropbox — optional)" />
            </div>
            {err && <div style={{gridColumn:"1 / -1", color:"var(--ach-red)", padding:8, background:"var(--ach-red-tint)", borderRadius:6, fontSize:12}}>{err}</div>}
          </div>
        </div>
        <div className="ach-modal__footer" style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button className="ach-btn ach-btn--grey" onClick={onClose} data-testid="am-cancel">Cancel</button>
          <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy || !f.unit_number.trim()} data-testid="am-save">
            {busy ? "Adding…" : "Add machine"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Machines() {
  const [machines, setMachines] = useState([]);
  const [hires, setHires] = useState({});
  const [customers, setCustomers] = useState({});
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  // Column sorting (added Sep 2026 evening — same behaviour as Hires and
  // Customers via src/lib/sortable.js).
  const { sort, toggleSort, sortArrow, applySort } = useSortable();
  const nav = useNavigate();
  const { user } = useAuth();

  const loadMachines = () => {
    Promise.all([
      api.get("/machines"),
      api.get("/hires?status_filter=active"),
      api.get("/customers"),
    ]).then(([m, h, c]) => {
      setMachines(m.data);
      const hById = {};
      h.data.forEach(x => { hById[x.machine_id] = x; });
      setHires(hById);
      const cById = {};
      c.data.forEach(x => { cById[x.id] = x; });
      setCustomers(cById);
    });
  };
  useEffect(() => { loadMachines(); }, []);

  const openMachine = (m) => nav(`/machine-details?unit=${encodeURIComponent(m.unit_number)}`);

  const filtered = machines.filter(m => {
    if (!q) return true;
    const s = q.toLowerCase();
    const c = hires[m.id] ? customers[hires[m.id].customer_id] : null;
    return (
      (m.unit_number || "").toLowerCase().includes(s) ||
      (m.serial || "").toLowerCase().includes(s) ||
      (m.make || "").toLowerCase().includes(s) ||
      (m.model_name || "").toLowerCase().includes(s) ||
      (m.owning_company || "").toLowerCase().includes(s) ||
      (m.location || "").toLowerCase().includes(s) ||
      (m.status || "").toLowerCase().includes(s) ||
      (c?.legal_name || "").toLowerCase().includes(s)
    );
  });

  // Sort AFTER filter so search narrows first, then sort orders. Per-column
  // value getter: unit uses the ACH-numeric key so ACH102 < ACH514, the rest
  // is standard lowercase-string / date-ISO / number.
  const sorted = applySort(filtered, (m, key) => {
    const h = hires[m.id];
    const c = h ? customers[h.customer_id] : null;
    switch (key) {
      case "unit":     return unitNumberKey(m.unit_number);
      case "model":    return `${m.make || ""} ${m.model_name || ""}`.trim().toLowerCase();
      case "capacity": return Number(m.capacity_tonnes) || 0;
      case "owner":    return (m.owning_company || "").toLowerCase();
      case "customer": return (c?.legal_name || "").toLowerCase();
      case "location": return (m.location || "").toLowerCase();
      case "status":   return (m.status || "").toLowerCase();
      case "dueback":  return h?.expected_end_date || "";
      case "monthly":  return h ? (Number(h.weekly_rate) * 4.33) : 0;
      case "rego":     return m.rego_expiry || "";
      case "cranesafe": return m.cranesafe_due || "";
      case "plantreg": return m.plant_reg_expiry || "";
      default:         return "";
    }
  });

  return (
    <div data-testid="machines-page">
      <PageHeader
        title="Machines"
        subtitle={`${machines.length} in the fleet across all six companies`}
        actions={(user?.role === "Director" || user?.role === "Office") && (
          <button className="ach-btn ach-btn--primary" onClick={()=>setAddOpen(true)} data-testid="btn-add-machine">+ Add a machine</button>
        )}
      />
      <div className="ach-card" data-testid="card-all-machines">
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
          <h2 className="ach-card__title" style={{margin:0}}>All machines — who has what</h2>
          <input placeholder="Search unit, model, customer…" value={q} onChange={(e)=>setQ(e.target.value)}
            data-testid="machines-search"
            style={{border:"1px solid var(--ach-card-border)", borderRadius:6, padding:"8px 12px", fontSize:13, width: 280}} />
        </div>
        <div className="ach-table-wrap">
          <table className="ach-table">
            <thead>
              <tr>
                {/* Same sort interaction as Hires and Customers — see src/lib/sortable.js */}
                <th onClick={()=>toggleSort("unit")}      style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-unit">Unit{sortArrow("unit")}</th>
                <th onClick={()=>toggleSort("model")}     style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-model">Model{sortArrow("model")}</th>
                <th onClick={()=>toggleSort("capacity")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-capacity">Cap.{sortArrow("capacity")}</th>
                <th onClick={()=>toggleSort("owner")}     style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-owner">Owned by{sortArrow("owner")}</th>
                <th onClick={()=>toggleSort("customer")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-customer">Customer{sortArrow("customer")}</th>
                <th onClick={()=>toggleSort("location")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-location">Where{sortArrow("location")}</th>
                <th onClick={()=>toggleSort("status")}    style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-status">Status{sortArrow("status")}</th>
                <th onClick={()=>toggleSort("dueback")}   style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-dueback">Due back{sortArrow("dueback")}</th>
                <th onClick={()=>toggleSort("monthly")}   className="money" style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-monthly">Monthly{sortArrow("monthly")}</th>
                <th onClick={()=>toggleSort("rego")}      style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-rego">Rego{sortArrow("rego")}</th>
                <th onClick={()=>toggleSort("cranesafe")} style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-cranesafe">CraneSafe{sortArrow("cranesafe")}</th>
                <th onClick={()=>toggleSort("plantreg")}  style={{cursor:"pointer", userSelect:"none"}} data-testid="machine-col-sort-plantreg">Plant reg{sortArrow("plantreg")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(m => {
                const h = hires[m.id];
                const c = h ? customers[h.customer_id] : null;
                const monthly = h ? h.weekly_rate * 4.33 : 0;
                return (
                  <tr key={m.id} data-testid={`machine-row-${m.unit_number}`}>
                    <td>
                      <button
                        type="button"
                        onClick={()=>openMachine(m)}
                        data-testid={`machine-unit-link-${m.unit_number}`}
                        style={{background:"none", border:"none", padding:0, cursor:"pointer", color:"var(--ach-maroon)", fontWeight:700, fontSize:"inherit", textDecoration:"underline"}}
                        className="unit-num">
                        {m.unit_number}
                      </button>
                      {m.folder_link && (
                        <a
                          href={m.folder_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e)=>e.stopPropagation()}
                          title={`Open shared folder for ${m.unit_number}`}
                          aria-label={`Open shared folder for ${m.unit_number}`}
                          data-testid={`machine-folder-link-${m.unit_number}`}
                          style={{marginLeft:6, display:"inline-flex", verticalAlign:"middle", color:"var(--ach-text-2)"}}
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      <div className="subtitle">{m.serial}</div>
                    </td>
                    <td>{m.make} {m.model_name}</td>
                    <td>{m.capacity_tonnes}t</td>
                    <td>{m.owning_company}</td>
                    <td>{c ? c.legal_name : <span style={{color:"var(--ach-text-2)"}}>—</span>}</td>
                    <td>{m.location || "—"}</td>
                    <td>{statusPill(m.status)}</td>
                    <td>{h && h.expected_end_date ? shortDate(h.expected_end_date) : "—"}</td>
                    <td className="money">{h ? money0(monthly) : <span style={{color:"var(--ach-text-2)"}}>—</span>}</td>
                    <td>{paperPill(m.rego_expiry)}</td>
                    <td>{paperPill(m.cranesafe_due)}</td>
                    <td>{paperPill(m.plant_reg_expiry)}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={12} style={{textAlign:"center", padding:24, color:"var(--ach-text-2)"}}>No matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {addOpen && <AddMachineDialog onClose={()=>setAddOpen(false)} onSaved={loadMachines} />}
    </div>
  );
}
